import React from 'react';
import { styled } from '@linaria/react';

import { range, normalize, clamp } from '@/utils';
import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';

import { lchToRgbString } from './lch.helpers';

const MAX_CHROMA = 100;
const NUM_OF_STOPS = 10;

const ChromaLitGrid = ({
  hue,
  size,
  chroma,
  setChroma,
  lightness,
  setLightness,
}) => {
  const id = React.useId().replace(/:/g, '');

  const wrapperRef = React.useRef();
  const [isMouseDown, setIsMouseDown] = React.useState(false);

  const [mousePosition, boundingBox] = useMousePositionWithinElement(
    wrapperRef,
    {
      buffer: 128,
      throttleBy: 20,
    }
  );

  // Calculate color values from mouse position
  React.useEffect(() => {
    if (!isMouseDown || !mousePosition || !boundingBox) {
      return;
    }

    setChroma(
      Math.round(
        clamp(
          normalize(
            mousePosition.x,
            0,
            boundingBox.width,
            0,
            MAX_CHROMA
          ),
          0,
          MAX_CHROMA
        )
      )
    );
    setLightness(
      Math.round(
        clamp(
          normalize(mousePosition.y, 0, boundingBox.height, 100, 0),
          0,
          100
        )
      )
    );
  }, [
    isMouseDown,
    mousePosition,
    boundingBox,
    setChroma,
    setLightness,
  ]);

  // Track dragging
  React.useEffect(() => {
    function handleMouseUp() {
      setIsMouseDown(false);
    }

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isMouseDown]);

  function handleKeyDown(ev) {
    let satIncrement = 0;
    let litIncrement = 0;

    switch (ev.key) {
      case 'ArrowUp': {
        ev.preventDefault();
        litIncrement = 5;
        break;
      }
      case 'ArrowDown': {
        ev.preventDefault();
        litIncrement = -5;
        break;
      }
      case 'ArrowLeft': {
        ev.preventDefault();
        satIncrement = -5;
        break;
      }
      case 'ArrowRight': {
        ev.preventDefault();
        satIncrement = +5;
        break;
      }
    }

    setChroma((sat) =>
      Math.round(clamp(sat + satIncrement, 0, MAX_CHROMA))
    );
    setLightness((lit) =>
      Math.round(clamp(lit + litIncrement, 0, 100))
    );
  }

  const x = normalize(chroma, 0, MAX_CHROMA, 0, size);
  const y = normalize(lightness, 0, 100, size, 0);

  const rgbColor = lchToRgbString(lightness, chroma, hue);

  // GRADIENT SOLUTION?
  // Vertically, I create N stops based on LCH color, at full chroma.
  // On top of that, I create a black-to-white gradient in SVG,
  // and use a mask to fade it out from left to right?

  const stops = range(NUM_OF_STOPS).map((index) => {
    const lightness = normalize(index, 0, NUM_OF_STOPS - 1, 0, 100);
    return lchToRgbString(lightness, MAX_CHROMA, hue);
  });

  const colorfulGradientId = `chroma-gradient-colorful-${id}`;
  const grayscaleGradientId = `chroma-gradient-grayscale-${id}`;
  const maskId = `chroma-mask-${id}`;

  return (
    <Wrapper
      style={{
        '--fallback-background': rgbColor,
      }}
    >
      <GridWrapper
        ref={wrapperRef}
        onMouseDown={() => setIsMouseDown(true)}
        onTouchStart={() => setIsMouseDown(true)}
        style={{
          width: size,
          height: size,
          '--hue': hue + 'deg',
          cursor: isMouseDown ? 'none' : undefined,
        }}
      >
        <ColorGradient
          key={hue}
          style={{
            backgroundImage: `linear-gradient(to top, ${stops.join(
              ', '
            )})`,
          }}
        />
        <ChromaSvg viewBox="0 0 250 250">
          <defs>
            <linearGradient
              id={colorfulGradientId}
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop
                offset="0%"
                style={{
                  stopColor: '#FFF',
                }}
              />
              <stop
                offset="100%"
                style={{
                  stopColor: '#000',
                }}
              />
            </linearGradient>
            <linearGradient
              id={grayscaleGradientId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop
                offset="0%"
                style={{
                  stopColor: '#FFF',
                }}
              />
              <stop
                offset="100%"
                style={{
                  stopColor: '#000',
                }}
              />
            </linearGradient>
            <mask id={maskId}>
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill={`url(#${grayscaleGradientId})`}
              />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill={`url(#${colorfulGradientId})`}
            mask={`url(#${maskId})`}
          />
        </ChromaSvg>
        <Indicator
          style={{
            '--x': `${x}px`,
            '--y': `${y}px`,
            cursor: isMouseDown ? 'none' : undefined,
          }}
          onKeyDown={handleKeyDown}
        />
      </GridWrapper>
      <SaturationAxis>
        <span>Chroma</span>
      </SaturationAxis>
      <LightnessAxis>
        <span>Lightness</span>
      </LightnessAxis>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const GridWrapper = styled.div`
  position: relative;
  border-radius: 2px;
  /* Hide corners */
  overflow: hidden;
  /* prettier-ignore */
  border: var(--white-border-thickness) solid white;
  /* prettier-ignore */
  box-shadow:
    0.6px 1.3px 2.2px rgba(0, 0, 0, 0.02),
    1.5px 3.1px 5.3px rgba(0, 0, 0, 0.028),
    2.9px 5.8px 10px rgba(0, 0, 0, 0.035),
    5.1px 10.3px 17.9px rgba(0, 0, 0, 0.042),
    9.6px 19.2px 33.4px rgba(0, 0, 0, 0.05),
    23px 46px 80px rgba(0, 0, 0, 0.07);
  user-select: none;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  cursor: pointer;
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  border-radius: 2px;
`;

const ChromaSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  border-radius: 2px;
`;

const ColorGradient = styled(GradientOverlay)`
  background: linear-gradient(
    to right,
    var(--fallback-gradient-from),
    var(--fallback-gradient-to)
  );

  @supports (background-color: lch(50% 50 50)) {
    background: linear-gradient(
      to right,
      lch(50% 132 var(--hue) / 0),
      lch(50% 132 var(--hue) / 1)
    );
  }
`;

const Indicator = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 24px;
  height: 24px;
  border: 2px solid black;
  background-color: var(--fallback-background);
  border-radius: 50%;
  /* prettier-ignore */
  transform: translate(
    calc(-50% + var(--x)),
    calc(-50% + var(--y))
  );
  transform-origin: center center;
  cursor: grab;
  box-shadow: inset 0px 0px 0px 2px white;
  user-select: none;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  @supports (background-color: lch(50% 50 50)) {
    background-color: var(--background);
  }

  &:active {
    cursor: none;
  }

  &:focus {
    outline-offset: 4px;
  }
`;

const Axis = styled.div`
  --height: 3rem;
  position: absolute;
  height: var(--height);
  display: flex;
  justify-content: center;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 25%;
    height: 1px;
    margin: auto;
    background: var(--color-gray-300);
  }

  &::before {
    left: 0;
  }
  &::after {
    right: 0;
  }

  span {
    line-height: var(--height);
    font-size: 0.875rem;
    text-transform: uppercase;
    color: var(--color-gray-600);
    font-weight: var(--font-weight-medium);
    user-select: none;
  }
`;

const SaturationAxis = styled(Axis)`
  top: 0;
  left: 0;
  right: 0;
  transform: translateY(-100%);
`;

const LightnessAxis = styled(Axis)`
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  transform: rotate(-90deg) translate(-100%, -100%);
  transform-origin: top left;
`;

export default ChromaLitGrid;
